import React, { forwardRef, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { CloseButton } from '../../../../../core/components'
import { Location } from '../../../FilterBarTypes'
import { ImageCard } from '../../ImageCard/ImageCard'
import { SectionHeader } from '../SectionHeader/SectionHeader'

import './Locations.css'

type Props = {
  locations?: Location[]
  selectedLocations: Location[]
  setSelectedLocations: (locations: Location[]) => void
  autoFocus?: boolean
  multiSelect?: boolean
  onClose?: () => void
}

export const Locations = forwardRef<HTMLDivElement, Props>(
  (
    {
      locations,
      selectedLocations,
      setSelectedLocations,
      autoFocus,
      multiSelect = false,
      onClose,
    },
    ref
  ) => {
    const { t } = useTranslation('filterBar')
    const firstCardRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (autoFocus && firstCardRef.current) {
        firstCardRef.current.focus()
      }
    }, [autoFocus])

    const handleLocationClick = (location: Location) => {
      if (multiSelect) {
        // Multi-select: toggle location in array
        const isSelected = selectedLocations.some(
          (loc) => loc.id === location.id
        )

        if (isSelected) {
          // Remove location if already selected
          setSelectedLocations(
            selectedLocations.filter((loc) => loc.id !== location.id)
          )
        } else {
          // Add location to selection
          setSelectedLocations([...selectedLocations, location])
        }
      } else {
        // Single-select: replace selection with clicked location and close modal
        setSelectedLocations([location])
        onClose?.()
      }
    }

    return (
      <div id="will-filter-bar-locations" ref={ref}>
        <SectionHeader
          title={t('locations.title')}
          action={onClose && <CloseButton handleClose={onClose} />}
        />

        <div className="will-locations-filter-container">
          {!!locations?.length &&
            locations.map((location, index) => {
              return (
                <ImageCard
                  key={location.id}
                  ref={index === 0 ? firstCardRef : null}
                  title={location.label}
                  description={location.description}
                  imageUrl={location.imageUrl}
                  isSelected={selectedLocations.some(
                    (loc) => loc.id === location.id
                  )}
                  onClick={() => handleLocationClick(location)}
                />
              )
            })}
        </div>
      </div>
    )
  }
)

Locations.displayName = 'Locations'
